// src/domains/finance/deals/pages/finance-deals-list-page.jsx
import FinanceDealsApi from 'api/admins/finance-deals-api'
import { TitlePage } from 'components/atoms'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useHistory, useLocation } from 'react-router'
import { PAYMENT_METHOD_FILTER_OPTIONS, SALES_METHOD_FILTER_OPTIONS } from 'domains/sales/deals/constants/sales-deals-constants'
import {
  Badge,
  Button,
  Card,
  Col,
  Row,
  Table,
  Pagination,
  PaginationItem,
  PaginationLink,
} from 'reactstrap'
import ParamsUtil from 'utils/params-util'

// -------------------- constants --------------------
const TZ_JKT = 'Asia/Jakarta'
const CANCELLED_STATES = new Set(['cancelled', 'canceled'])
const FAILED_STATES = new Set(['failed', 'fail', 'payment_failed', 'failed_payment'])

// Fetching strategy:
// We fetch multiple pages from index so export contains all rows (within safety limits).
const FETCH_PER_PAGE = 200
const MAX_FETCH_PAGES = 20 // safety: max deals fetched = FETCH_PER_PAGE * MAX_FETCH_PAGES

const UI_PER_PAGE_OPTIONS = [10, 25, 50, 100, 200]

// Scope values must match Finance::DealsController#index
const SCOPE_OPTIONS = [
  { value: 'need_packing', label: 'Need Packing' },
  { value: 'ready_to_ship', label: 'Ready to Ship' },
  { value: 'sent', label: 'Sent / Completed' },
  { value: 'all', label: 'All' },
]

const CUSTOMER_DEAL_TYPE_OPTIONS = [
  { value: '', label: 'All Customer Type' },
  { value: 'new_customer', label: 'New Customer' },
  { value: 'old_customer', label: 'Old Customer' },
]

const INTERNAL_CATEGORY_OPTIONS = [
  { value: '', label: 'All Internal Categories' },
  { value: 'H', label: 'H - Hermès' },
  { value: 'C', label: 'C - Curated' },
  { value: 'W', label: 'W - Watch' },
]

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'Bags', label: 'Bags' },
  { value: 'Shoes', label: 'Shoes' },
  { value: 'Accessories', label: 'Accessories' },
  { value: 'Watch', label: 'Watch' },
  { value: 'Ready to Wear', label: 'Ready to Wear' },
]

const CUSTOMER_DEAL_TYPE_LABEL_BY_VALUE = CUSTOMER_DEAL_TYPE_OPTIONS.reduce((acc, o) => {
  if (o.value) acc[o.value] = o.label
  return acc
}, {})

const INTERNAL_CATEGORY_LABEL_BY_VALUE = INTERNAL_CATEGORY_OPTIONS.reduce((acc, o) => {
  if (o.value) acc[o.value] = o.label
  return acc
}, {})

const CATEGORY_LABEL_BY_VALUE = CATEGORY_OPTIONS.reduce((acc, o) => {
  if (o.value) acc[o.value] = o.label
  return acc
}, {})

const customerDealTypeBadgeColor = (type) => {
  if (type === 'old_customer') return 'warning'
  if (type === 'new_customer') return 'success'
  return 'secondary'
}

const getCustomerDealTypeLabel = (type, fallbackLabel = '') => (
  fallbackLabel || CUSTOMER_DEAL_TYPE_LABEL_BY_VALUE[type] || ''
)

// ✅ NEW: Sales Method options (single-select)
// const SALES_METHOD_OPTIONS = [
//   { value: '', label: 'All Sales Methods' },
//   { value: 'BC TELE', label: 'BC TELE' },
//   { value: 'BC WA Community', label: 'BC WA Community' },
//   { value: 'Cari Barang', label: 'Cari Barang' },
//   { value: 'Catalog IG', label: 'Catalog IG' },
//   { value: 'Catalog Web', label: 'Catalog Web' },
//   { value: 'Deal of the Week', label: 'Deal of the Week' },
//   { value: 'Event Notta', label: 'Event Notta' },
//   { value: 'Home Shopping', label: 'Home Shopping' },
//   { value: 'IG Channel', label: 'IG Channel' },
//   { value: 'IG Content - Catalogue di Rak', label: 'IG Content - Catalogue di Rak' },
//   { value: 'IG Content - Foto di Toko', label: 'IG Content - Foto di Toko' },
//   { value: 'IG Content - Try On', label: 'IG Content - Try On' },
//   { value: 'IG Story - WA ME', label: 'IG Story - WA ME' },
//   { value: 'Private Viewing', label: 'Private Viewing' },
//   { value: 'Regular Offline', label: 'Regular Offline' },
//   { value: 'Regular Purchase - Offline', label: 'Regular Purchase - Offline' },
//   { value: 'Regular Purchase - Online', label: 'Regular Purchase - Online' },
//   { value: 'Self-Checkout Online', label: 'Self-Checkout Online' },
//   { value: 'Tawarin Barang - Online', label: 'Tawarin Barang - Online' },
//   { value: 'Walk In', label: 'Walk In' },
//   { value: 'Warehouse Sale', label: 'Warehouse Sale' },
//   { value: 'WH Sale Feb 2026', label: 'WH Sale Feb 2026' },
// ]

// ✅ NEW: Deals State options (multi-select via CHECKBOX LIST)
const DEAL_STATE_OPTIONS = [
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'on_delivery', label: 'On Delivery' },
  { value: 'on_sources', label: 'On Sources' },
  { value: 'sourced', label: 'Sourced' },
  { value: 'waiting_for_confirmation', label: 'Waiting for Confirmation' },
  { value: 'waiting_for_payment', label: 'Waiting for Payment' },
  { value: 'waiting_for_shipment', label: 'Waiting for Shipment' },
]

const DEAL_STATE_LABEL_BY_VALUE = DEAL_STATE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label
  return acc
}, {})

const PAYMENT_METHOD_LABEL_BY_VALUE = PAYMENT_METHOD_FILTER_OPTIONS.reduce((acc, o) => {
  if (o.value) acc[o.value] = o.label
  return acc
}, {})

const DEAL_STATE_VALUE_SET = new Set(DEAL_STATE_OPTIONS.map((o) => o.value))
const ALL_DEAL_STATE_VALUES = DEAL_STATE_OPTIONS.map((o) => o.value)

// EXACT CSV headers requested (order matters)
const CSV_HEADERS = [
  'deal_id',
  'reference_number',
  'created_at_jkt_time',
  'sales_method',
  'email',
  'buyer_name',
  'customer_type',
  'consignor_name',
  'voucher_used',
  'voucher_code',
  'voucher_discount_amount',
  'buyer_phone',
  'ship_to_name',
  'ship_to_phone',
  'ship_to_address',
  'ship_to_suburb',
  'ship_to_city',
  'ship_to_province',
  'sa_deal',
  'note',
  'sa_assist',
  'sa_invoice',
  'item_code',
  'item_name',
  'item_note',
  'internal_category',
  'category_name',
  'condition_name',
  'default_price',
  'selling_price',
  'sales_price',
  'buying_price',
  'payment_method',
  'payment_card_type',
  'payment_state',
  'deal_state',
]

const CSV_HEADER_LABELS = {
  customer_type: 'Customer Type',
}

// -------------------- helpers --------------------
const safeStr = (v) => (v == null ? '' : String(v))

const shouldShowPaymentCardType = (paymentMethod, cardType) => {
  const normalizedMethod = safeStr(paymentMethod)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')

  const normalizedCardType = safeStr(cardType).trim()

  return ['edc_bri', 'edc_bca'].includes(normalizedMethod) && normalizedCardType !== ''
}

const isPlainObject = (v) =>
  v != null && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date)

const pickFirst = (obj, keys = []) => {
  if (!isPlainObject(obj)) return ''
  for (const k of keys) {
    const v = obj[k]
    if (v != null && String(v).trim() !== '') return String(v).trim()
  }
  return ''
}

const normalizeText = (v) => {
  if (v == null) return ''
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  if (Array.isArray(v)) return v.map(normalizeText).filter(Boolean).join(', ')
  if (isPlainObject(v)) {
    // last resort: stringify object so it is not [object Object]
    try { return JSON.stringify(v) } catch (_) { return '' }
  }
  return String(v)
}

const looksLikeJsonString = (s) => {
  if (typeof s !== 'string') return false
  const t = s.trim()
  return (t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))
}

const tryParseJson = (s) => {
  if (!looksLikeJsonString(s)) return null
  try {
    return JSON.parse(s)
  } catch (_) {
    return null
  }
}

const normalizeShipToName = (v) => {
  // parse JSON string if needed
  if (typeof v === 'string') {
    const parsed = tryParseJson(v)
    if (parsed) return normalizeShipToName(parsed)
    return v.trim() || '-'
  }

  // object case
  if (isPlainObject(v)) {
    const name =
      pickFirst(v, [
        'recipient_name',
        'name',
        'full_name',
        'recipient',
        'customer_name',
        'first_name',
        'firstname',
        'last_name',
        'lastname',
      ]) || ''

    // ✅ if object has no usable name → return "-"
    return name.trim() ? name.trim() : '-'
  }

  // other types
  const s = normalizeText(v)
  return s.trim() ? s : '-'
}

const normalizeShipToPhone = (v) => {
  if (typeof v === 'string') return v
  if (isPlainObject(v)) {
    return pickFirst(v, ['recipient_number', 'phone', 'phone_number', 'mobile', 'recipient_phone']) || ''
  }
  return normalizeText(v)
}

const normalizeShipToAddress = (v) => {
  // If it is already a normal string, return it (unless it's JSON string)
  if (typeof v === 'string') {
    const parsed = tryParseJson(v)
    if (parsed) return normalizeShipToAddress(parsed) // parse JSON and normalize again
    return v
  }

  // If it's an array, try each entry until one works
  if (Array.isArray(v)) {
    for (const x of v) {
      const s = normalizeShipToAddress(x)
      if (s && s.trim()) return s
    }
    return ''
  }

  // If it's not a plain object, fallback to safe string
  if (!isPlainObject(v)) return normalizeText(v)

  // 1) Direct full address candidates
  const direct = pickFirst(v, [
    'complete_address',
    'full_address',
    'address',
    'street',
    'address_line',
    'address_line_1',
    'address1',
    'destination',
    'address_detail',
  ])
  if (direct) return direct

  // 2) Sometimes address is nested: { address_detail: {...} } or { destination: {...} }
  for (const nestedKey of ['address_detail', 'destination', 'customer_address', 'address_obj']) {
    const nested = v[nestedKey]
    const s = normalizeShipToAddress(nested)
    if (s && s.trim()) return s
  }

  // 3) Compose from parts (common Address model fields)
  const line1 = pickFirst(v, ['street', 'address', 'complete_address'])
  const suburb = pickFirst(v, ['suburb', 'subdistrict', 'kelurahan', 'district'])
  const city = pickFirst(v, ['city', 'city_name', 'kabupaten'])
  const province = pickFirst(v, ['province', 'province_name', 'state'])
  const postal = pickFirst(v, ['postal_code', 'zip'])

  const composed = [line1, suburb, city, province, postal].filter(Boolean).join(', ')
  if (composed) return composed

  // 4) If we still cannot build anything meaningful, do NOT dump JSON
  // Returning empty is better than showing object dump in CSV.
  return ''
}

const toNumOrBlank = (v) => {
  if (v == null || v === '') return ''
  const n = Number(v)
  return Number.isFinite(n) ? n : safeStr(v)
}

const formatIDR = (v) => {
  const n = Number(v)
  if (!Number.isFinite(n) || n <= 0) return ''
  return `Rp ${n.toLocaleString('id-ID')}`
}

const getVoucherSummary = (deal) => {
  const methodSummary = isPlainObject(deal?.voucher_summary) ? deal.voucher_summary : {}

  const rawVoucherDetails = deal?.voucher_details
  let parsedVoucherDetails = null

  if (isPlainObject(rawVoucherDetails)) {
    parsedVoucherDetails = rawVoucherDetails
  } else if (typeof rawVoucherDetails === 'string') {
    parsedVoucherDetails = tryParseJson(rawVoucherDetails)
  }

  const code =
    safeStr(methodSummary?.code).trim() ||
    safeStr(deal?.voucher_code).trim() ||
    pickFirst(parsedVoucherDetails, ['code']) ||
    ''

  const discount = Number(methodSummary?.discount_amount ?? deal?.discount_amount ?? 0)
  const safeDiscount = Number.isFinite(discount) ? discount : 0

  const used =
    Boolean(methodSummary?.used) ||
    Boolean(code) ||
    Boolean(deal?.voucher_id) ||
    safeDiscount > 0

  return {
    used,
    code,
    discount_amount: safeDiscount,
  }
}

// For Excel compatibility (UTF-8)
const withBom = (s) => `\uFEFF${s}`

// Always-quote CSV cell (simple + safe)
const csvCell = (v) => `"${safeStr(v).replace(/"/g, '""')}"`

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

// Format ISO datetime -> "YYYY-MM-DD HH:mm:ss" in Asia/Jakarta using Intl
function fmtJktDateTime(iso) {
  if (!iso) return ''

  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''

  try {
    return d.toLocaleString('sv-SE', {
      timeZone: TZ_JKT,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
  } catch (_) {
    // Fallback only when Intl formatting fails
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: TZ_JKT,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(d)

    const map = {}
    parts.forEach((p) => {
      map[p.type] = p.value
    })

    return `${map.year}-${map.month}-${map.day} ${map.hour}:${map.minute}:${map.second}`
  }
}

// Convert query value -> input datetime-local value
function toDateTimeLocalValue(v) {
  if (!v) return ''
  const s = String(v).trim()
  if (!s) return ''
  if (s.includes('T')) return s.slice(0, 16)
  return s.replace(' ', 'T').slice(0, 16)
}

// Convert input datetime-local -> query param string
function fromDateTimeLocalValue(v) {
  const s = String(v || '').trim()
  if (!s) return ''
  return s
}

function parseSearch(search) {
  return Object.fromEntries(new URLSearchParams(search))
}

/**
 * ✅ Deals State parsing / formatting
 * URL format:
 *   deal_states=cancelled,failed
 * Component format:
 *   draft.deal_states = ['cancelled','failed']
 */
function parseDealStatesParam(v) {
  if (v == null) return []
  if (Array.isArray(v)) {
    return v
      .map((x) => safeStr(x).trim())
      .filter(Boolean)
      .filter((s) => DEAL_STATE_VALUE_SET.has(s))
  }

  const s = safeStr(v).trim()
  if (!s) return []

  return s
    .split(',')
    .map((x) => safeStr(x).trim())
    .filter(Boolean)
    .filter((x) => DEAL_STATE_VALUE_SET.has(x))
}

function stringifyDealStatesParam(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return ''
  const out = []
  const seen = new Set()
  for (const x of arr) {
    const s = safeStr(x).trim()
    if (!s) continue
    if (!DEAL_STATE_VALUE_SET.has(s)) continue
    if (seen.has(s)) continue
    seen.add(s)
    out.push(s)
  }
  return out.join(',')
}

function formatDealStatesForDisplay(v) {
  const list = parseDealStatesParam(v)
  if (!list.length) return ''
  return list.map((s) => DEAL_STATE_LABEL_BY_VALUE[s] || s).join(', ')
}

/**
 * Build querystring safely.
 * ✅ Important: removes empty string / null / undefined consistently.
 * (We still keep ParamsUtil for compatibility, but harden it.)
 */
function buildSearch(params) {
  const trimmed = Object.fromEntries(
    Object.entries(params || {}).map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
  )

  // ParamsUtil may not remove "" reliably in some projects, so we enforce it:
  const hardCleaned = Object.fromEntries(Object.entries(trimmed).filter(([_, v]) => v !== '' && v != null))

  const cleaned = ParamsUtil.removeEmptyAttributes ? ParamsUtil.removeEmptyAttributes(hardCleaned) || {} : hardCleaned

  return new URLSearchParams(cleaned).toString()
}

// Flatten deals -> per-item rows for table + CSV
function flattenDealsToRows(deals = [], internalCategoryFilter = '', categoryNameFilter = '') {
  const normalizedInternalCategoryFilter = safeStr(internalCategoryFilter).trim().toUpperCase()
  const normalizedCategoryNameFilter = safeStr(categoryNameFilter).trim().toLowerCase()

  const out = []

  for (const deal of deals) {
    const user = deal?.user || {}

    // ✅ prefer deal.user_detail (selected detail for this deal), fallback to user.detail
    const detail = deal?.user_detail || user?.detail || {}
    const shipment = deal?.shipment || {}
    const payment = deal?.payment || {}
    const voucher = getVoucherSummary(deal)

    const assist =
      safeStr(deal?.assist_by) ||
      (Array.isArray(deal?.assist_by_list) ? deal.assist_by_list.filter(Boolean).join(', ') : '')

    const baseRow = {
      deal_id: deal?.id ?? '',
      reference_number: deal?.reference_number ?? '',
      created_at_jkt_time: fmtJktDateTime(deal?.created_at),
      sales_method: deal?.sales_method ?? '',
      sales_method_note: deal?.sales_method_note ?? '',
      email: user?.email ?? '',
      buyer_name: detail?.name ?? '',
      customer_deal_type: deal?.customer_deal_type ?? '',
      customer_type: getCustomerDealTypeLabel(
        deal?.customer_deal_type,
        deal?.customer_deal_type_label
      ),
      customer_deal_type_label: getCustomerDealTypeLabel(
        deal?.customer_deal_type,
        deal?.customer_deal_type_label
      ),
      customer_valid_deals_count: deal?.customer_valid_deals_count ?? '',
      consignor_name: '',
      voucher_used: voucher.used,
      voucher_code: voucher.code,
      voucher_discount_amount: voucher.discount_amount,
      buyer_phone: deal?.customer_phone ?? detail?.phone ?? '',

      ship_to_name: normalizeShipToName(shipment?.recipient),
      ship_to_phone: normalizeShipToPhone(shipment?.recipient) || '',
      ship_to_address:
        normalizeShipToAddress(shipment?.address_detail) ||
        normalizeShipToAddress(shipment?.destination) ||
        normalizeShipToAddress(detail?.complete_address) ||
        '',

      ship_to_suburb: '',
      ship_to_city: detail?.city_name ?? '',
      ship_to_province: '',

      sa_deal: deal?.deal_by ?? '',
      note: deal?.note ?? '',
      sa_assist: assist,
      sa_invoice: deal?.sales_name_in_invoice ?? '',

      payment_method: payment?.method_type ?? '',
      payment_card_type: payment?.card_type ?? '',
      payment_state: payment?.state ?? '',
      deal_state: deal?.state ?? '',
    }

    const cartItems = deal?.cart?.cart_items || []

    if (Array.isArray(cartItems) && cartItems.length > 0) {
      for (const ci of cartItems) {
        const p = ci?.product || {}

        const consignment = p?.consignment || {}
        const consignorName =
          consignment?.consignor_detail?.name ||
          consignment?.user?.detail?.name ||
          p?.supplier_name ||
          ''

        const designerName = p?.designer?.name ?? p?.designer_name ?? ''
        const categoryName = p?.category?.name ?? p?.category_name ?? ''
        const conditionName = p?.condition?.name ?? p?.condition_name ?? ''

        const internalCategory = safeStr(p?.internal_category).trim().toUpperCase()
        if (normalizedInternalCategoryFilter && internalCategory !== normalizedInternalCategoryFilter) {
          continue
        }
        if (normalizedCategoryNameFilter && safeStr(categoryName).trim().toLowerCase() !== normalizedCategoryNameFilter) {
          continue
        }

        const sellingPrice = p?.selling_price ?? ''
        const salesPrice = ci?.price ?? ci?.final_price ?? ci?.selling_price ?? ''

        out.push({
          ...baseRow,
          consignor_name: consignorName,
          item_code: p?.sku ?? '',
          item_name: p?.name ?? '',
          item_note: ci?.note ?? '',
          default_price: p?.default_price ?? '',
          selling_price: sellingPrice,
          internal_category: internalCategory,
          designer_name: designerName,
          category_name: categoryName,
          condition_name: conditionName,

          size: p?.size ?? '',
          leather: p?.leather ?? '',
          hardware: p?.hardware ?? '',
          p_colour: p?.p_colour ?? '',
          p_size: p?.p_size ?? '',
          height: p?.height ?? '',
          length: p?.length ?? '',
          width: p?.width ?? '',
          strap_drop: p?.strap_drop ?? '',
          handle_drop: p?.handle_drop ?? '',
          comes_with: p?.comes_with ?? '',
          shipping: p?.shipping ?? '',
          defects: p?.defects ?? '',
          qty: Number(ci?.quantity || 1),

          sales_price: salesPrice,
          buying_price: p?.buying_price ?? '',
        })
      }
      continue
    }

    const skuList = deal?.sku_list
    if (Array.isArray(skuList) && skuList.length > 0) {
      for (const sku of skuList) {
        out.push({
          ...baseRow,
          item_code: sku,
          item_name: '',
          item_note: '',
          default_price: '',
          internal_category: '',
          designer_name: '',
          category_name: '',
          condition_name: '',
          size: '',
          leather: '',
          hardware: '',
          p_colour: '',
          p_size: '',
          height: '',
          length: '',
          width: '',
          strap_drop: '',
          handle_drop: '',
          comes_with: '',
          shipping: '',
          defects: '',
          qty: '',
          selling_price: '',
          sales_price: '',
          buying_price: '',
        })
      }
      continue
    }

    out.push({
      ...baseRow,
      item_code: '',
      item_name: '',
      item_note: '',
      default_price: '',
      internal_category: '',
      designer_name: '',
      category_name: '',
      condition_name: '',
      size: '',
      leather: '',
      hardware: '',
      p_colour: '',
      p_size: '',
      height: '',
      length: '',
      width: '',
      strap_drop: '',
      handle_drop: '',
      comes_with: '',
      shipping: '',
      defects: '',
      qty: '',
      selling_price: '',
      sales_price: '',
      buying_price: '',
    })
  }

  return out
}

// Fetch all pages (bounded) so export includes all deals within selected filters
async function fetchAllDealsFromIndex(baseParams) {
  const all = []
  let meta = null

  for (let page = 1; page <= MAX_FETCH_PAGES; page += 1) {
    const res = await FinanceDealsApi.get({
      ...baseParams,
      page,
      per_page: FETCH_PER_PAGE,
    })

    const data = res?.data?.data || []
    meta = res?.data?.meta || meta

    all.push(...data)

    const totalPages = Number(meta?.total_pages || 1)
    if (page >= totalPages) break
    if (!data.length) break
  }

  const totalPages = Number(meta?.total_pages || 1)
  const truncated = totalPages > MAX_FETCH_PAGES

  return { deals: all, meta, truncated }
}

// -------------------- UI sub-component --------------------
const DateTimeRangeFilter = ({ fromValue, toValue, onChangeFrom, onChangeTo }) => {
  return (
    <div className="d-flex align-items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
      <div style={{ flex: '0 0 auto' }}>
        <div className="small text-muted mb-1">Created From (JKT)</div>
        <input
          type="datetime-local"
          className="form-control"
          value={fromValue || ''}
          onChange={(e) => onChangeFrom(e.target.value)}
        />
      </div>

      <div className="text-muted" style={{ paddingTop: 18 }}>
        to
      </div>

      <div style={{ flex: '0 0 auto' }}>
        <div className="small text-muted mb-1">Created To (JKT)</div>
        <input
          type="datetime-local"
          className="form-control"
          value={toValue || ''}
          onChange={(e) => onChangeTo(e.target.value)}
        />
      </div>
    </div>
  )
}

DateTimeRangeFilter.propTypes = {
  fromValue: PropTypes.string,
  toValue: PropTypes.string,
  onChangeFrom: PropTypes.func,
  onChangeTo: PropTypes.func,
}

// -------------------- layout-only helper (pagination window) --------------------
function buildPageWindow(current, total, windowSize = 7) {
  if (total <= 1) return [1]
  const size = Math.max(3, windowSize)
  if (total <= size) return Array.from({ length: total }, (_, i) => i + 1)

  const half = Math.floor(size / 2)
  let start = Math.max(1, current - half)
  let end = Math.min(total, start + size - 1)
  start = Math.max(1, end - size + 1)

  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}

// -------------------- main page --------------------
const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg: PropTypes.func,
    setApiErrorMsg: PropTypes.func,
  }),
}

const FinanceDealsListPage = ({ pageUtils }) => {
  const history = useHistory()
  const location = useLocation()

  const urlFilters = useMemo(() => parseSearch(location.search), [location.search])
  const pathName = location.pathname

  const [draft, setDraft] = useState(() => ({
    scope: urlFilters?.scope || 'all',
    sales_method: urlFilters?.sales_method || '',
    sales_method_note: urlFilters?.sales_method_note || '',
    payment_method: urlFilters?.payment_method || '',
    customer_deal_type: urlFilters?.customer_deal_type || '',
    internal_category: urlFilters?.internal_category || '',
    category_name: urlFilters?.category_name || '',
    deal_states: parseDealStatesParam(urlFilters?.deal_states),
    has_voucher_only: urlFilters?.has_voucher_only === '1',
    q: urlFilters?.q || '',
    name: urlFilters?.name || '',
    consignor_name: urlFilters?.consignor_name || '',
    email: urlFilters?.email || '',
    phone: urlFilters?.phone || '',
    order_number: urlFilters?.order_number || '',
    sku: urlFilters?.sku || '',
    created_from: toDateTimeLocalValue(urlFilters?.created_from || ''),
    created_to: toDateTimeLocalValue(urlFilters?.created_to || ''),
  }))

  useEffect(() => {
    setDraft({
      scope: urlFilters?.scope || 'all',
      sales_method: urlFilters?.sales_method || '',
      sales_method_note: urlFilters?.sales_method_note || '',
      payment_method: urlFilters?.payment_method || '',
      customer_deal_type: urlFilters?.customer_deal_type || '',
      internal_category: urlFilters?.internal_category || '',
      category_name: urlFilters?.category_name || '',
      deal_states: parseDealStatesParam(urlFilters?.deal_states),
      has_voucher_only: urlFilters?.has_voucher_only === '1',
      q: urlFilters?.q || '',
      name: urlFilters?.name || '',
      consignor_name: urlFilters?.consignor_name || '',
      email: urlFilters?.email || '',
      phone: urlFilters?.phone || '',
      order_number: urlFilters?.order_number || '',
      sku: urlFilters?.sku || '',
      created_from: toDateTimeLocalValue(urlFilters?.created_from || ''),
      created_to: toDateTimeLocalValue(urlFilters?.created_to || ''),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  const [deals, setDeals] = useState([])
  const [serverMeta, setServerMeta] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loadWarning, setLoadWarning] = useState('')

  const [page, setPage] = useState(1)
  const [per, setPer] = useState(UI_PER_PAGE_OPTIONS[1]) // default 25
  const [sortBy, setSortBy] = useState({ key: 'created_at_jkt_time', dir: 'desc' })
  const [exporting, setExporting] = useState(false)

  // ✅ FIX: Safe draft field setter (extracts value immediately; never uses pooled event later)
  const setDraftField = useCallback((key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }))
  }, [])

  const onChangeDraftField = useCallback(
    (key) => (e) => {
      const v = e?.target?.value ?? ''
      setDraftField(key, v)
    },
    [setDraftField]
  )

  // ✅ NEW: Deal State checkbox helpers (Option A)
  const toggleDealState = useCallback((value) => {
    const v = safeStr(value).trim()
    if (!v) return
    if (!DEAL_STATE_VALUE_SET.has(v)) return

    setDraft((prev) => {
      const curr = Array.isArray(prev?.deal_states) ? prev.deal_states : []
      const set = new Set(curr.map((x) => safeStr(x).trim()).filter(Boolean))

      if (set.has(v)) set.delete(v)
      else set.add(v)

      // keep stable order based on DEAL_STATE_OPTIONS
      const nextArr = ALL_DEAL_STATE_VALUES.filter((x) => set.has(x))
      return { ...prev, deal_states: nextArr }
    })
  }, [])

  const selectAllDealStates = useCallback(() => {
    setDraftField('deal_states', [...ALL_DEAL_STATE_VALUES])
  }, [setDraftField])

  const clearDealStates = useCallback(() => {
    setDraftField('deal_states', [])
  }, [setDraftField])

  const aliveRef = useRef(true)
  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
    }
  }, [])

  useEffect(() => {
    const run = async () => {
      setIsLoading(true)
      setLoadWarning('')

      try {
        const baseParams =
          ParamsUtil.removeEmptyAttributes({
            scope: urlFilters?.scope || 'all',

            // ✅ Deals State filter (comma-separated string)
            deal_states: urlFilters?.deal_states,

            // Sales Method filter
            sales_method: urlFilters?.sales_method,
            sales_method_note: urlFilters?.sales_method_note,
            payment_method: urlFilters?.payment_method,
            customer_deal_type: urlFilters?.customer_deal_type,
            internal_category: urlFilters?.internal_category,
            category_name: urlFilters?.category_name,

            has_voucher_only: urlFilters?.has_voucher_only === '1' ? '1' : undefined,

            q: urlFilters?.q,
            name: urlFilters?.name,
            consignor_name: urlFilters?.consignor_name || '',
            email: urlFilters?.email,
            phone: urlFilters?.phone,
            order_number: urlFilters?.order_number,
            sku: urlFilters?.sku,

            created_from: urlFilters?.created_from,
            created_to: urlFilters?.created_to,

            tz: TZ_JKT,
            include_items: '1',

            sort_by: urlFilters?.sort_by || 'deals.created_at',
            sort_dir: urlFilters?.sort_dir || 'desc',
          }) || {}

        const { deals: allDeals, meta, truncated } = await fetchAllDealsFromIndex(baseParams)

        if (!aliveRef.current) return

        setDeals(allDeals)
        setServerMeta(meta)

        if (truncated) {
          setLoadWarning(
            `⚠️ Loaded only first ${MAX_FETCH_PAGES} pages (max ${MAX_FETCH_PAGES * FETCH_PER_PAGE} deals). ` +
              `Please narrow the filters (especially date range) to export safely.`
          )
        } else if (Number(meta?.total_pages || 1) > 1) {
          setLoadWarning(`Loaded ${allDeals.length} deals across ${meta.total_pages} page(s) for export/table.`)
        }

        setPage(1)
      } catch (error) {
        if (!aliveRef.current) return
        const msg = error?.response?.data || error?.message || String(error)
        pageUtils?.setApiErrorMsg?.(msg)
      } finally {
        if (!aliveRef.current) return
        setIsLoading(false)
      }
    }

    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  const rows = useMemo(
    () => flattenDealsToRows(deals, urlFilters?.internal_category, urlFilters?.category_name),
    [deals, urlFilters?.internal_category, urlFilters?.category_name]
  )

  const sortedRows = useMemo(() => {
    const list = [...rows]
    const { key, dir } = sortBy

    const cmp = (a, b) => {
      const av = a?.[key]
      const bv = b?.[key]

      if (typeof av === 'number' && typeof bv === 'number') {
        return dir === 'asc' ? av - bv : bv - av
      }

      const as = safeStr(av).toLowerCase()
      const bs = safeStr(bv).toLowerCase()
      if (as < bs) return dir === 'asc' ? -1 : 1
      if (as > bs) return dir === 'asc' ? 1 : -1
      return 0
    }

    list.sort(cmp)
    return list
  }, [rows, sortBy])

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / per))
  const pageSafe = Math.min(page, totalPages)

  const pagedRows = useMemo(() => {
    const start = (pageSafe - 1) * per
    return sortedRows.slice(start, start + per)
  }, [sortedRows, pageSafe, per])

  const toggleSort = (key) => {
    setSortBy((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' }
      return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
    })
    setPage(1)
  }

  const normalizedUrlFiltersForCompare = () => ({
    scope: urlFilters?.scope || 'all',
    sales_method: urlFilters?.sales_method || '',
    sales_method_note: urlFilters?.sales_method_note || '',
    payment_method: urlFilters?.payment_method || '',
    customer_deal_type: urlFilters?.customer_deal_type || '',
    internal_category: urlFilters?.internal_category || '',
    category_name: urlFilters?.category_name || '',
    deal_states: urlFilters?.deal_states || '',
    has_voucher_only: urlFilters?.has_voucher_only === '1' ? '1' : '',
    q: urlFilters?.q || '',
    name: urlFilters?.name || '',
    consignor_name: urlFilters?.consignor_name || '',
    email: urlFilters?.email || '',
    phone: urlFilters?.phone || '',
    order_number: urlFilters?.order_number || '',
    sku: urlFilters?.sku || '',
    created_from: urlFilters?.created_from || '',
    created_to: urlFilters?.created_to || '',
  })

  const handleApply = () => {
    const next = {
      scope: safeStr(draft.scope).trim(),
      sales_method: safeStr(draft.sales_method).trim(),
      sales_method_note: safeStr(draft.sales_method_note).trim(),
      payment_method: safeStr(draft.payment_method).trim(),
      customer_deal_type: safeStr(draft.customer_deal_type).trim(),
      internal_category: safeStr(draft.internal_category).trim().toUpperCase(),
      category_name: safeStr(draft.category_name).trim(),

      // ✅ Deal states as comma-separated string in URL
      deal_states: stringifyDealStatesParam(draft.deal_states),

      has_voucher_only: draft.has_voucher_only ? '1' : '',

      q: safeStr(draft.q).trim(),
      name: safeStr(draft.name).trim(),
      consignor_name: safeStr(draft.consignor_name).trim(),
      email: safeStr(draft.email).trim(),
      phone: safeStr(draft.phone).trim(),
      order_number: safeStr(draft.order_number).trim(),
      sku: safeStr(draft.sku).trim(),
      created_from: fromDateTimeLocalValue(draft.created_from),
      created_to: fromDateTimeLocalValue(draft.created_to),
    }

    const nextSearch = buildSearch(next)
    const currentSearch = buildSearch(normalizedUrlFiltersForCompare())

    if (nextSearch === currentSearch) return

    history.push({
      pathname: pathName,
      search: nextSearch ? `?${nextSearch}` : '',
    })
  }

  const resetFilters = () => {
    setDraft({
      scope: 'all',
      sales_method: '',
      sales_method_note: '',
      payment_method: '',
      customer_deal_type: '',
      internal_category: '',
      category_name: '',
      deal_states: [],
      has_voucher_only: false,
      q: '',
      name: '',
      consignor_name: '',
      email: '',
      phone: '',
      order_number: '',
      sku: '',
      created_from: '',
      created_to: '',
    })
    history.push({ pathname: pathName })
  }

  const recordFinanceDealsCsvDownload = async () => {
    const params = ParamsUtil.removeEmptyAttributes({
      scope: urlFilters?.scope || 'all',
      deal_states: urlFilters?.deal_states,
      sales_method: urlFilters?.sales_method,
      sales_method_note: urlFilters?.sales_method_note,
      payment_method: urlFilters?.payment_method,
      customer_deal_type: urlFilters?.customer_deal_type,
      internal_category: urlFilters?.internal_category,
      category_name: urlFilters?.category_name,
      has_voucher_only: urlFilters?.has_voucher_only === '1' ? '1' : undefined,
      q: urlFilters?.q,
      name: urlFilters?.name,
      consignor_name: urlFilters?.consignor_name || '',
      email: urlFilters?.email,
      phone: urlFilters?.phone,
      order_number: urlFilters?.order_number,
      sku: urlFilters?.sku,
      created_from: urlFilters?.created_from,
      created_to: urlFilters?.created_to,
      tz: TZ_JKT,
      include_items: '0',
      report_download: '1',
      page: 1,
      per_page: 1,
      sort_by: urlFilters?.sort_by || 'deals.created_at',
      sort_dir: urlFilters?.sort_dir || 'desc',
    }) || {}

    try {
      await FinanceDealsApi.get(params)
    } catch (_) {}
  }

  const exportCSV = async () => {
    try {
      setExporting(true)
      await recordFinanceDealsCsvDownload()

      const lines = []
    lines.push(
      CSV_HEADERS.map((h) => csvCell(CSV_HEADER_LABELS[h] || h)).join(',')
    )

      for (const r of sortedRows) {
        const line = CSV_HEADERS.map((h) => {
          switch (h) {
            case 'sales_price':
            case 'selling_price':
            case 'buying_price':
            case 'default_price':
            case 'voucher_discount_amount':
              return csvCell(toNumOrBlank(r[h]))

            case 'voucher_used':
              return csvCell(r.voucher_used ? 'Yes' : 'No')

            default:
              return csvCell(r[h])
          }
        }).join(',')
        lines.push(line)
      }

      const csv = withBom(lines.join('\n'))

      const fromLabel = draft.created_from ? safeStr(draft.created_from).replace(/[:T]/g, '-') : 'NA'
      const toLabel = draft.created_to ? safeStr(draft.created_to).replace(/[:T]/g, '-') : 'NA'
      const filename = `finance_deals_${fromLabel}_to_${toLabel}.csv`

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      downloadBlob(blob, filename)
    } finally {
      setExporting(false)
    }
  }

  const canExport = sortedRows.length > 0 && !isLoading && !exporting

  const dealStateCounts = useMemo(() => {
    const list = Array.isArray(sortedRows) ? sortedRows : [] // ✅ per-item rows
    let cancelled = 0
    let failed = 0

    for (const r of list) {
      const s = String(r?.deal_state || '').toLowerCase().trim()
      if (CANCELLED_STATES.has(s)) cancelled += 1
      else if (FAILED_STATES.has(s)) failed += 1
    }

    const total = list.length // ✅ items count
    const processing = Math.max(0, total - cancelled - failed)

    return { total, cancelled, failed, processing }
  }, [sortedRows])

  // -------------------- UI-only derived values --------------------
  const appliedFilters = [
    ['Scope', safeStr(urlFilters?.scope || 'all')],
    ['Sales Method', safeStr(urlFilters?.sales_method)],
    ['Influencer Name', safeStr(urlFilters?.sales_method_note)],
    ['Payment Method', PAYMENT_METHOD_LABEL_BY_VALUE[safeStr(urlFilters?.payment_method)] || safeStr(urlFilters?.payment_method)],
    ['Customer Type', CUSTOMER_DEAL_TYPE_LABEL_BY_VALUE[safeStr(urlFilters?.customer_deal_type)] || safeStr(urlFilters?.customer_deal_type)],
    ['Internal Category', INTERNAL_CATEGORY_LABEL_BY_VALUE[safeStr(urlFilters?.internal_category)] || safeStr(urlFilters?.internal_category)],
    ['Category', CATEGORY_LABEL_BY_VALUE[safeStr(urlFilters?.category_name)] || safeStr(urlFilters?.category_name)],
    ['Deal State', formatDealStatesForDisplay(urlFilters?.deal_states)],
    ['Voucher', urlFilters?.has_voucher_only === '1' ? 'Has voucher only' : ''],
    ['Search', safeStr(urlFilters?.q)],
    ['Customer', safeStr(urlFilters?.name)],
    ['Consignor', safeStr(urlFilters?.consignor_name)],
    ['Email', safeStr(urlFilters?.email)],
    ['Phone', safeStr(urlFilters?.phone)],
    ['Order', safeStr(urlFilters?.order_number)],
    ['SKU', safeStr(urlFilters?.sku)],
    ['From', safeStr(urlFilters?.created_from)],
    ['To', safeStr(urlFilters?.created_to)],
    ['Rows/Page', safeStr(per)],
  ].filter(([_, v]) => v.trim() !== '')

  const pageWindow = buildPageWindow(pageSafe, totalPages, 7)

  const StatBox = ({ label, value }) => (
    <div
      style={{
        background: '#000',
        borderRadius: 10,
        padding: '10px 14px',
        minWidth: 120,
        textAlign: 'center',
        lineHeight: 1.1,
      }}
    >
      <div style={{ color: '#fff', fontSize: 11, opacity: 0.85, textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ color: '#4da3ff', fontSize: 26, fontWeight: 800, marginTop: 4 }}>
        {value}
      </div>
    </div>
  )

  const selectedDealStatesCount = Array.isArray(draft?.deal_states) ? draft.deal_states.length : 0

  // -------------------- UI/layout (Sales-style) --------------------
  return (
    <>
      <TitlePage mainTitle={'Deals'} subTitle={'List'} />

      <Row>
        {/* =========================
            LEFT SIDEBAR (Filters) — match Sales layout
           ========================= */}
        <Col lg="3" md="12" className="mb-3">
          <Card className="p-3">

            {/* Sidebar actions (Sales-style placement) */}
            <div className="d-flex justify-content-center" style={{ gap: 10, marginBottom: 20 }}>
              <Button color="primary" onClick={handleApply} disabled={isLoading || exporting}>
                {isLoading ? 'Loading…' : 'Filter'}
              </Button>
              <Button color="danger" outline onClick={resetFilters} disabled={isLoading || exporting}>
                Reset
              </Button>
            </div>

            {/* Date range */}
            <div className="mb-3">
              <DateTimeRangeFilter
                fromValue={draft.created_from}
                toValue={draft.created_to}
                onChangeFrom={(v) => setDraftField('created_from', v)}
                onChangeTo={(v) => setDraftField('created_to', v)}
              />
              <div className="small text-muted mt-1">Tip: For “today (JKT)”, set From 00:00 and To 23:59.</div>
            </div>

            {/* ✅ Sales Method */}
            <div className="mb-3">
              <div className="small text-muted mb-1">Sales Method</div>
              <select
                className="form-control"
                value={draft.sales_method || ''}
                onChange={onChangeDraftField('sales_method')}
              >
                {/* {SALES_METHOD_OPTIONS.map((o) => ( */}
                {SALES_METHOD_FILTER_OPTIONS.map((o) => (
                  <option key={o.value || '__all_sales_method__'} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Customer Type */}
            <div className="mb-3">
              <div className="small text-muted mb-1">Customer Type</div>
              <select
                className="form-control"
                value={draft.customer_deal_type || ''}
                onChange={onChangeDraftField('customer_deal_type')}
              >
                {CUSTOMER_DEAL_TYPE_OPTIONS.map((o) => (
                  <option key={o.value || '__all_customer_deal_type__'} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Internal Category */}
            <div className="mb-3">
              <div className="small text-muted mb-1">Internal Category</div>
              <select
                className="form-control"
                value={draft.internal_category || ''}
                onChange={onChangeDraftField('internal_category')}
              >
                {INTERNAL_CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value || '__all_internal_category__'} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Category */}
            <div className="mb-3">
              <div className="small text-muted mb-1">Category</div>
              <select
                className="form-control"
                value={draft.category_name || ''}
                onChange={onChangeDraftField('category_name')}
              >
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value || '__all_category__'} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            {/* ✅ Sales Method Note */}
            <div className="mb-3">
              <div className="small text-muted mb-1">Influencer Name</div>
              <input
                className="form-control"
                value={draft.sales_method_note || ''}
                onChange={onChangeDraftField('sales_method_note')}
                placeholder="nama influencer / akun IG"
              />
            </div>

            {/* ✅ Payment Method */}
            <div className="mb-3">
              <div className="small text-muted mb-1">Payment Method</div>
              <select
                className="form-control"
                value={draft.payment_method || ''}
                onChange={onChangeDraftField('payment_method')}
              >
                {PAYMENT_METHOD_FILTER_OPTIONS.map((o) => (
                  <option key={o.value || '__all_payment_method__'} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Has Voucher Only */}
            <div className="mb-3">
              <div className="form-check" style={{ paddingLeft: 0 }}>
                <label
                  className="d-flex align-items-center mb-0"
                  style={{ gap: 10, cursor: 'pointer', userSelect: 'none' }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(draft.has_voucher_only)}
                    onChange={(e) => setDraftField('has_voucher_only', e.target.checked)}
                    disabled={isLoading || exporting}
                  />
                  <span className="mb-0">Has voucher only</span>
                </label>
              </div>
            </div>

            {/* ✅ UPDATED: Deals State Filter — Option A (Checkbox list + Select All / Clear) */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1" style={{ gap: 10 }}>
                <div className="small text-muted">Deal State</div>

                <div className="d-flex align-items-center" style={{ gap: 10 }}>
                  <Button
                    color="link"
                    size="sm"
                    className="p-0"
                    onClick={selectAllDealStates}
                    disabled={isLoading || exporting}
                    title="Centang semua state"
                    style={{ fontSize: 12, textDecoration: 'none' }}
                  >
                    Select All
                  </Button>
                  <Button
                    color="link"
                    size="sm"
                    className="p-0"
                    onClick={clearDealStates}
                    disabled={isLoading || exporting}
                    title="Kosongkan pilihan state (tampilkan semua deals)"
                    style={{ fontSize: 12, textDecoration: 'none' }}
                  >
                    Clear
                  </Button>
                </div>
              </div>

              <div className="border rounded p-2" style={{ background: '#fff' }}>
                {DEAL_STATE_OPTIONS.map((o) => {
                  const id = `deal_state__${o.value}`
                  const checked = Array.isArray(draft?.deal_states) ? draft.deal_states.includes(o.value) : false
                  return (
                    <div key={o.value} className="form-check">
                      <input
                        id={id}
                        className="form-check-input"
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleDealState(o.value)}
                        disabled={isLoading || exporting}
                      />
                      <label className="form-check-label" htmlFor={id}>
                        {o.label}
                      </label>
                    </div>
                  )
                })}
              </div>

              <div className="small text-muted mt-1">
                Selected: <b>{selectedDealStatesCount}</b>. Jika <b>kosong</b>, sistem akan menampilkan <b>semua deals</b>.
              </div>
            </div>

            {/* Per Page */}
            <div className="mb-3">
              <div className="small text-muted mb-1">Per Page</div>
              <select
                className="form-control"
                value={per}
                onChange={(e) => {
                  const v = Number(e.target.value)
                  setPer(Number.isFinite(v) ? v : UI_PER_PAGE_OPTIONS[1])
                  setPage(1)
                }}
              >
                {UI_PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort (UI-only control mapped to existing sortBy) */}
            <div className="mb-3">
              <div className="small text-muted mb-1">Sort</div>
              <select
                className="form-control"
                value={`${sortBy.key}:${sortBy.dir}`}
                onChange={(e) => {
                  const [key, dir] = String(e.target.value).split(':')
                  if (!key || !dir) return
                  setSortBy({ key, dir })
                  setPage(1)
                }}
              >
                <option value="created_at_jkt_time:desc">Created (Latest)</option>
                <option value="created_at_jkt_time:asc">Created (Oldest)</option>
                <option value="deal_id:desc">Deal ID (High → Low)</option>
                <option value="deal_id:asc">Deal ID (Low → High)</option>
              </select>
            </div>

            {/* Search */}
            <div className="mb-3">
              <div className="small text-muted mb-1">Search</div>
              <input
                className="form-control"
                placeholder="reference number, tracking, customer, recipient, SKU…"
                value={draft.q || ''}
                onChange={onChangeDraftField('q')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleApply()
                  }
                }}
              />
            </div>

            {/* Customer Name */}
            <div className="mb-3">
              <div className="small text-muted mb-1">Customer Name</div>
              <input
                className="form-control"
                value={draft.name || ''}
                onChange={onChangeDraftField('name')}
                placeholder="user_details.name"
              />
            </div>

            {/* Consignor Name */}
            <div className="mb-3">
              <div className="small text-muted mb-1">Consignor Name</div>
              <input
                className="form-control"
                value={draft.consignor_name || ''}
                onChange={onChangeDraftField('consignor_name')}
                placeholder="consignor name / supplier"
              />
            </div>

            {/* Email */}
            <div className="mb-3">
              <div className="small text-muted mb-1">Email</div>
              <input
                className="form-control"
                value={draft.email || ''}
                onChange={onChangeDraftField('email')}
                placeholder="users.email"
              />
            </div>

            {/* Phone */}
            <div className="mb-3">
              <div className="small text-muted mb-1">Phone Number</div>
              <input
                className="form-control"
                value={draft.phone || ''}
                onChange={onChangeDraftField('phone')}
                placeholder="user_details.phone"
              />
            </div>

            {/* Order Number */}
            <div className="mb-3">
              <div className="small text-muted mb-1">Order Number</div>
              <input
                className="form-control"
                value={draft.order_number || ''}
                onChange={onChangeDraftField('order_number')}
                placeholder="deals.reference_number"
              />
            </div>

            {/* SKU */}
            <div className="mb-3">
              <div className="small text-muted mb-1">SKU (Item Dalam Deal)</div>
              <input
                className="form-control"
                value={draft.sku || ''}
                onChange={onChangeDraftField('sku')}
                placeholder="products.sku"
              />
            </div>

            {/* Status/Scope */}
            <div className="mb-3">
              <div className="small text-muted mb-1">Status</div>
              <select
                className="form-control"
                value={draft.scope || 'all'}
                onChange={onChangeDraftField('scope')}
              >
                {SCOPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Sidebar actions (Sales-style placement) */}
            <div className="d-flex justify-content-center" style={{ gap: 10 }}>
              <Button color="primary" onClick={handleApply} disabled={isLoading || exporting}>
                {isLoading ? 'Loading…' : 'Filter'}
              </Button>
              <Button color="danger" outline onClick={resetFilters} disabled={isLoading || exporting}>
                Reset
              </Button>
            </div>
          </Card>
        </Col>

        {/* =========================
            RIGHT CONTENT (Summary + Applied Filters + Table)
           ========================= */}
        <Col lg="9" md="12">
          {/* Top info bar (includes Export) */}
          <Card className="p-3 mb-3">
            <div className="d-flex justify-content-between align-items-start" style={{ gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div className="font-weight-bold">Deals (per Item)</div>
                <div className="small text-muted">
                  Rows: {sortedRows.length} • Deals loaded: {deals.length}
                  {serverMeta?.total_count != null ? ` • Server total: ${serverMeta.total_count}` : ''}
                </div>
              </div>

              {/* Stats + Export (top-right) */}
              <div className="d-flex align-items-start" style={{ gap: 12, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <div className="d-flex" style={{ gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <StatBox label="All Items" value={dealStateCounts.total} />
                  <StatBox label="Cancelled Items" value={dealStateCounts.cancelled} />
                  <StatBox label="Failed Items" value={dealStateCounts.failed} />
                  <StatBox label="Processing Items" value={dealStateCounts.processing} />
                </div>

                <Button
                  color="secondary"
                  outline
                  onClick={exportCSV}
                  disabled={!canExport}
                  title={sortedRows.length ? 'Export all loaded rows to CSV' : 'No rows to export'}
                  style={{ height: 44 }}
                >
                  {exporting ? 'Preparing…' : 'Export CSV'}
                </Button>
              </div>
            </div>

            {/* Applied filters */}
            <div className="mt-3">
              <div className="small text-muted mb-2">APPLIED FILTERS</div>
              <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                {appliedFilters.length ? (
                  appliedFilters.map(([k, v]) => (
                    <Badge key={`${k}:${v}`} color="secondary" pill>
                      {k}: {v}
                    </Badge>
                  ))
                ) : (
                  <span className="small text-muted">None</span>
                )}
              </div>
            </div>

            {loadWarning ? (
              <div className="mt-2">
                <Badge color="warning" pill>
                  {loadWarning}
                </Badge>
              </div>
            ) : null}
          </Card>

          {/* Table */}
          <Card className="p-2">
            <div className="d-flex justify-content-between align-items-center px-2 py-2" style={{ gap: 12, flexWrap: 'wrap' }}>
              <div className="small text-muted">
                Showing {pagedRows.length} of {sortedRows.length} row(s) • Page {pageSafe}/{totalPages}
              </div>
            </div>

            <div className="px-2 pb-2">
              <div className="small text-muted">
                <strong>Customer Type Rules:</strong>
                <ul className="mb-0 pl-3">
                  <li>New Customer = belum memiliki valid history sebelumnya</li>
                  <li>Old Customer = sudah memiliki valid history sebelumnya</li>
                  <li>Valid history = previous deal dengan state minimal waiting for shipment dan payment settlement</li>
                  <li>Current checkout tetap dihitung walaupun belum completed / belum CF finance</li>
                  <li>Previous Valid Deal dihitung dari valid history sebelum deal yang sedang ditampilkan</li>
                </ul>
              </div>
            </div>

            <div className="table-responsive">
              <Table size="sm" hover striped className="mb-0">
                <thead>
                  <tr>
                    <th role="button" onClick={() => toggleSort('deal_id')}>
                      Deal ID {sortBy.key === 'deal_id' ? (sortBy.dir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th role="button" onClick={() => toggleSort('reference_number')}>
                      Reference {sortBy.key === 'reference_number' ? (sortBy.dir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th role="button" onClick={() => toggleSort('created_at_jkt_time')}>
                      Created (JKT) {sortBy.key === 'created_at_jkt_time' ? (sortBy.dir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                    <th>Buyer</th>
                    <th>Customer Type</th>
                    <th>Consignor</th>
                    <th>Sales Method</th>
                    <th>Sales Method Note</th>
                    <th>Voucher</th>
                    <th>Item Code</th>
                    <th>Item Name</th>
                    <th>Internal Category</th>
                    <th>Category</th>
                    <th className="text-right">Qty</th>
                    <th className="text-right">Default Price</th>
                    <th className="text-right">Selling Price</th>
                    <th className="text-right">Sales Price</th>
                    <th className="text-right">Buying Price</th>
                    <th>Payment</th>
                    <th>Deal State</th>
                  </tr>
                </thead>

                <tbody>
                  {pagedRows.map((r, idx) => (
                    <tr key={`${r.deal_id}:${r.item_code || 'x'}:${idx}`} style={{ cursor: 'default' }}>
                      <td className="text-monospace">{r.deal_id}</td>
                      <td className="text-monospace">{r.reference_number}</td>
                      <td>{r.created_at_jkt_time}</td>
                      <td>
                        <div className="font-weight-bold">{r.buyer_name || '—'}</div>
                        <div className="small text-muted">{r.email || ''}</div>
                        <div className="small text-muted">{r.buyer_phone || ''}</div>
                      </td>
                      <td>
                        {r.customer_deal_type_label ? (
                          <>
                            <Badge color={customerDealTypeBadgeColor(r.customer_deal_type)} pill>
                              {r.customer_deal_type_label}
                            </Badge>
                            <div className="small text-muted mt-1">
                              Previous Valid Deal: {safeStr(r.customer_valid_deals_count || 0)}
                            </div>
                          </>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        <div className="font-weight-bold">{r.consignor_name || '—'}</div>
                      </td>
                      <td>
                        <div className="font-weight-bold">{r.sales_method || '—'}</div>
                      </td>
                      <td>
                        {r.sales_method_note ? (
                          <div>{r.sales_method_note}</div>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {r.voucher_used ? (
                          <>
                            <Badge color="info" pill>
                              {r.voucher_code || 'USED'}
                            </Badge>
                            {Number(r.voucher_discount_amount) > 0 ? (
                              <div className="small text-muted">
                                - {formatIDR(r.voucher_discount_amount)}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-monospace">{r.item_code || '—'}</td>
                      <td>{r.item_name || '—'}</td>
                      <td>{r.internal_category || '—'}</td>
                      <td>{r.category_name || '—'}</td>
                      <td className="text-right">{safeStr(r.qty)}</td>
                      <td className="text-right">{safeStr(r.default_price)}</td>
                      <td className="text-right">{safeStr(r.selling_price)}</td>
                      <td className="text-right">{safeStr(r.sales_price)}</td>
                      <td className="text-right">{safeStr(r.buying_price)}</td>
                      <td>
                      <div className="small">{r.payment_method || '—'}</div>
                        {shouldShowPaymentCardType(r.payment_method, r.payment_card_type) ? (
                          <div className="text-muted" style={{ fontSize: 11, lineHeight: 1.3 }}>
                            {r.payment_card_type}
                          </div>
                        ) : null}
                        <div className="small text-muted">{r.payment_state || ''}</div>
                      </td>
                      <td>{r.deal_state || '—'}</td>
                    </tr>
                  ))}

                  {!isLoading && pagedRows.length === 0 ? (
                    <tr>
                      <td colSpan={20} className="text-center text-muted py-4">
                        No rows found. Adjust filters, then click Filter.
                      </td>
                    </tr>
                  ) : null}

                  {isLoading ? (
                    <tr>
                      <td colSpan={20} className="text-center text-muted py-4">
                        Loading…
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </Table>
            </div>

            {/* Bottom pagination */}
            <div className="d-flex justify-content-center py-3">
              <Pagination size="sm" className="mb-0">
                <PaginationItem disabled={pageSafe <= 1}>
                  <PaginationLink
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      setPage((p) => Math.max(1, p - 1))
                    }}
                  >
                    ‹
                  </PaginationLink>
                </PaginationItem>

                {pageWindow.map((p) => (
                  <PaginationItem key={p} active={p === pageSafe}>
                    <PaginationLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        setPage(p)
                      }}
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                ))}

                <PaginationItem disabled={pageSafe >= totalPages}>
                  <PaginationLink
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      setPage((p) => Math.min(totalPages, p + 1))
                    }}
                  >
                    ›
                  </PaginationLink>
                </PaginationItem>
              </Pagination>
            </div>
          </Card>
        </Col>
      </Row>
    </>
  )
}

FinanceDealsListPage.propTypes = propTypes
export default FinanceDealsListPage
